<template>
    <div class="event-selector">
        <div class="event-selector-toolbar">
            <div class="field">
                <label>{{ $t("automation.automationName") }}: </label>
                <a-input v-model="automationName" :placeholder="$t('automation.automationName')" />
            </div>
        </div>

        <div class="module-chips">
            <div
                v-for="module in modules"
                :key="module"
                :class="['module-chip', { active: selectedModule === module }]"
                @click="selectedModule = module"
            >
                <a-icon :type="getModuleIcon(module).antIcon" v-if="getModuleIcon(module).antIcon" class="chip-icon" />
                <i class="material-icons chip-icon" v-else-if="getModuleIcon(module).materialIcon">{{
                    getModuleIcon(module).materialIcon
                }}</i>
                <component
                    :is="getModuleIcon(module).customIcon"
                    class="chip-icon custom-icon"
                    v-else-if="getModuleIcon(module).customIcon"
                />
                <span class="chip-text">{{ getModuleDisplayName(module) }}</span>
            </div>
        </div>
        <a-divider />
        <SupsisTable
            :columns="getCols()"
            :rows="getEvents()"
            top-pagination
            empty-data-bordered
            bottom-pagination
            :pagination-cursor="pagination"
            :options="{
                checkboxOption,
                rowStyleOption,
                clipboardOption,
                fixedHeader: true,
                id: 'events-table',
                borderX: true,
                borderY: true,
            }"
            @cursor-changed="(cursor) => (pagination = cursor)"
        />

        <!-- Message Matched Configuration Modal -->
        <MessageMatchedConfig
            :visible="showMessageMatchedConfigModal"
            v-model="messageMatchedConfig"
            :ok-text="$t('settings.create')"
            :cancel-text="$t('message.cancelText')"
            @save="saveMessageMatchedConfig"
            @cancel="cancelMessageMatchedConfig"
        />
    </div>
</template>
<script>
import Vue from "vue";
import SupsisTable from "../../../../components/SupsisTable.vue";
import moment from "moment";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import FormIcon from "../../../../components/svg/Form.vue";
import TicketIcon from "../../../../components/svg/Ticket.vue";
import CalendarIcon from "../../../../components/svg/Calendar.vue";
import ChatbotIcon from "../../../../components/svg/Chatbot.vue";
import MailIcon from "../../../../components/svg/Mail.vue";
import TaskIcon from "../../../../components/svg/icons/TaskIcon.vue";
import Comments from "../../../../components/svg/Comment";
import CustomPicker from "../../../../components/pickers/CustomPicker.vue";
import Modal2 from "../../../../components/common/Modal2.vue";
import MessageMatchedConfig from "./MessageMatchedConfig.vue";

export default Vue.extend({
    name: "EventSelector",
    components: {
        SupsisTable,
        SupsisButton,
        FormIcon,
        TicketIcon,
        TaskIcon,
        CalendarIcon,
        ChatbotIcon,
        MailIcon,
        Comments,
        CustomPicker,
        Modal2,
        MessageMatchedConfig,
    },
    props: {
        defaultModule: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            automationName: "İsimsiz Otomasyon 🚀",
            selectedModule: this.defaultModule || "all",
            selectedEvent: null,
            showMessageMatchedConfigModal: false,
            messageMatchedConfig: {
                author: "all",
                messageType: "all",
                platform: "all",
                channels: [],
                textFilter: {
                    type: "contains",
                    value: "",
                    caseSensitive: false,
                },
            },
            modules: [
                "all",
                "chat",
                "contact",
                // "tag",
                "message",
                "email",
                "task",
                // "event",
                "form",
                "meta-lead",
                "agent",
                "invoice",
                "whatsapp",
                "reservation",
                "chatbot",
                "webhook",
                "table",
                "ticket",
                "calendar",
                "voiceagent",
                "webphone",
                "channel",
                "youtube",
                "google_business",
                "google_play",
                "app_store",
                "google_drive",
            ].filter((module) => module === this.defaultModule || !this.defaultModule),
            events: [
                {
                    module: "voiceagent",
                    event: "voiceagent.call_started",
                    implemented: true,
                    description: this.$t("automation.whenVoiceAgentCallStarted"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "voiceagent",
                    event: "voiceagent.call_ended",
                    implemented: true,
                    description: this.$t("automation.whenVoiceAgentCallEnded"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "voiceagent",
                    event: "voiceagent.call_analyzed",
                    implemented: true,
                    description: this.$t("automation.whenVoiceAgentCallAnalyzed"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "webphone",
                    event: "webphone.call_event",
                    implemented: true,
                    description: "Webphone olay oluştuğunda",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "webphone",
                    event: "webphone.call_ringing",
                    implemented: true,
                    description: "Webphone çağrı geldiğinde",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "webphone",
                    event: "webphone.call_answered",
                    implemented: true,
                    description: "Webphone araması cevaplandığında",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "webphone",
                    event: "webphone.call_ended",
                    implemented: true,
                    description: "Webphone araması sonlandığında",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "webphone",
                    event: "webphone.inbound_call",
                    implemented: true,
                    description: "Gelen webphone araması",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "webphone",
                    event: "webphone.outbound_call",
                    implemented: true,
                    description: "Giden webphone araması",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "webphone",
                    event: "webphone.device_state_changed",
                    implemented: true,
                    description: "Webphone cihaz durumu değiştiğinde",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "webphone",
                    event: "webphone.device_ringing",
                    implemented: true,
                    description: "Webphone cihaz çağrıda",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "webphone",
                    event: "webphone.device_in_use",
                    implemented: true,
                    description: "Webphone cihaz kullanımda",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "webphone",
                    event: "webphone.device_available",
                    implemented: true,
                    description: "Webphone cihaz mevcut",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "channel",
                    event: "channel.status_changed",
                    implemented: true,
                    description: "Kanal bağlantı durumu değiştiğinde (sadece whatsapp v3 , linkedin ve telegram kanallarında geçerlidir)",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "channel",
                    event: "channel.disconnected",
                    implemented: true,
                    description: "Kanal bağlantısı koptuğunda (Yeniden bağlanma gerekir) (sadece whatsapp v3 , linkedin ve telegram kanallarında geçerlidir)",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "form",
                    event: "form.submitted",
                    implemented: true,
                    description: this.$t("automation.whenFormSubmitted"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "calendar",
                    event: "calendar.event_created",
                    implemented: true,
                    description: this.$t("automation.calendarEventCreated"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "calendar",
                    event: "calendar.event_updated",
                    implemented: true,
                    description: this.$t("automation.calendarEventUpdated"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "calendar",
                    event: "calendar.event_deleted",
                    implemented: true,
                    description: this.$t("automation.calendarEventDeleted"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "calendar",
                    event: "calendar.reservation_created",
                    implemented: true,
                    description: this.$t("automation.reservationCreated"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "calendar",
                    event: "calendar.reservation_date_changed",
                    implemented: true,
                    description: this.$t("automation.reservationDateChanged"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "calendar",
                    event: "calendar.reservation_deleted",
                    implemented: true,
                    description: this.$t("automation.reservationDeleted"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "table",
                    event: "table.record_created",
                    implemented: true,
                    description: this.$t("automation.whenRecordCreated"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "table",
                    event: "table.record_updated",
                    implemented: true,
                    description: this.$t("automation.whenRecordUpdated"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "table",
                    event: "table.record_deleted",
                    implemented: true,
                    description: this.$t("automation.whenRecordDelete"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "table",
                    event: "table.record_automation_button_clicked",
                    implemented: true,
                    description: this.$t("automation.whenRecordAutomation"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "message",
                    event: "message.picked",
                    implemented: true,
                    description: this.$t("automation.whenMessagePicked"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "message",
                    event: "message.matched",
                    implemented: true,
                    description: "Mesaj alındığında (filtreli)",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "webhook",
                    event: "webhook.catch",
                    implemented: false,
                    description: this.$t("automation.whenWebhookTriggered"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "chat",
                    event: "chat.created",
                    implemented: true,
                    description: this.$t("automation.whenChatCreated"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "chat",
                    event: "chat.closed",
                    implemented: true,
                    description: this.$t("automation.whenChatClosed"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "chat",
                    event: "chat.rated",
                    implemented: true,
                    description: this.$t("automation.whenChatRated"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                //chat.lost
                {
                    module: "chat",
                    event: "chat.lost",
                    implemented: true,
                    description: this.$t("automation.chatLost"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                //chat.webchat_completed etc.... instagram,facebook,messenger,whatsapp,telegram
                {
                    module: "chat",
                    event: "chat.webchat_closed",
                    implemented: true,
                    description: this.$t("automation.webchatCompleted"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "chat",
                    event: "chat.instagram_closed",
                    implemented: true,
                    description: this.$t("automation.instagramCompleted"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "chat",
                    event: "chat.messenger_closed",
                    implemented: true,
                    description: this.$t("automation.messengerCompleted"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "chat",
                    event: "chat.whatsapp_closed",
                    implemented: true,
                    description: this.$t("automation.whatsappCompleted"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "chat",
                    event: "chat.telegram_closed",
                    implemented: true,
                    description: this.$t("automation.telegramCompleted"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                //chat.assigned_to_agent
                {
                    module: "chat",
                    event: "chat.assigned_to_agent",
                    implemented: true,
                    description: this.$t("automation.chatAssignedToAgent"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },

                {
                    module: "contact",
                    event: "contact.created",
                    implemented: true,
                    description: this.$t("automation.whenNewContactCreated"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "contact",
                    event: "contact.updated",
                    implemented: true,
                    description: this.$t("automation.whenContactUpdate"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "contact",
                    event: "contact.contact-property-updated",
                    implemented: true,
                    description: this.$t("automation.whenContactPropUpdate"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "contact",
                    event: "contact.tag-added",
                    implemented: true,
                    description: this.$t("automation.whenNewTagAdded"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "contact",
                    event: "contact.tag-removed",
                    implemented: true,
                    description: this.$t("automation.whenTagRemoved"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                //contact.profile_button_clicked
                {
                    module: "contact",
                    event: "contact.profile_button_clicked",
                    implemented: true,
                    description: this.$t("automation.profileButtonClicked"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "contact",
                    event: "contact.ad_viewed",
                    implemented: true,
                    description: "Müşteri reklam görüntülediğinde",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "email",
                    event: "email.received",
                    implemented: true,
                    description: this.$t("automation.whenNewEmailReceived"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "email",
                    event: "email.delivered",
                    implemented: true,
                    description: "E-posta başarıyla teslim edildiğinde",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "email",
                    event: "email.bounced",
                    implemented: true,
                    description: "E-posta geri döndüğünde (bounce)",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "email",
                    event: "email.complained",
                    implemented: true,
                    description: "E-posta spam olarak işaretlendiğinde",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "email",
                    event: "email.clicked",
                    implemented: true,
                    description: "E-postadaki bir bağlantıya tıklandığında",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "email",
                    event: "email.opened",
                    implemented: true,
                    description: "E-posta açıldığında",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "email",
                    event: "email.sent",
                    implemented: true,
                    description: "E-posta gönderildiğinde",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "email",
                    event: "email.rejected",
                    implemented: true,
                    description: "E-posta reddedildiğinde",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "task",
                    event: "task.created",
                    implemented: true,
                    description: this.$t("automation.whenNewTaskCreated"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },

                {
                    module: "task",
                    event: "task.completed",
                    implemented: true,
                    description: this.$t("automation.whenTaskCompleted"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "task",
                    event: "task.deleted",
                    implemented: true,
                    description: this.$t("automation.whenTaskDeleted"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "task",
                    event: "task.assignee-changed",
                    implemented: true,
                    description: this.$t("automation.whenTaskAssigneeChanged"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "task",
                    event: "task.priority-changed",
                    implemented: true,
                    description: this.$t("automation.whenTaskPriorityChanged"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "task",
                    event: "task.due-date-changed",
                    implemented: true,
                    description: this.$t("automation.whenTaskDueDateChanged"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "task",
                    event: "task.stage-changed",
                    implemented: true,
                    description: this.$t("automation.whenTaskStatusChanged"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "task",
                    event: "task.archived",
                    implemented: true,
                    description: this.$t("automation.whenTaskArchived"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "task",
                    event: "task.tag-added",
                    implemented: true,
                    description: this.$t("automation.whenTagAdded"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "task",
                    event: "task.tag-removed",
                    implemented: true,
                    description: this.$t("automation.whenTagRemovedTask"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "task",
                    event: "task.checklist-completed",
                    implemented: true,
                    description: this.$t("automation.whenTagChecklist"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "task",
                    event: "task.reminder-activated",
                    implemented: false,
                    description: this.$t("automation.whenReminderActivated"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "ticket",
                    event: "ticket.created",
                    implemented: true,
                    description: this.$t("automation.whenTicketCreated"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "ticket",
                    event: "ticket.stage-changed",
                    implemented: true,
                    description: this.$t("automation.whenTicketStageChanged"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "ticket",
                    event: "ticket.assignee-changed",
                    implemented: true,
                    description: this.$t("automation.whenTicketAssigneeChanged"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "ticket",
                    event: "ticket.closed",
                    implemented: true,
                    description: this.$t("automation.whenTicketClosed"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "ticket",
                    event: "ticket.updated",
                    implemented: true,
                    description: this.$t("automation.whenTicketUpdated"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "meta-lead",
                    event: "meta-lead.created",
                    implemented: true,
                    description: this.$t("automation.whenNewMetaleadCreated"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "agent",
                    event: "agent.created",
                    implemented: true,
                    description: this.$t("automation.whenNewAgentCreated"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "agent",
                    event: "agent.online",
                    implemented: true,
                    description: this.$t("automation.whenAgentOnline"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "agent",
                    event: "agent.offline",
                    implemented: true,
                    description: this.$t("automation.whenAgentOffline"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "agent",
                    event: "agent.status.changed",
                    implemented: true,
                    description: this.$t("automation.whenAgentStatusChanged"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "agent",
                    event: "agent.login",
                    implemented: true,
                    description: this.$t("automation.whenAgentLogin"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "invoice",
                    event: "invoice.created",
                    implemented: false,
                    description: this.$t("automation.whenNewInvoiceCreated"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "whatsapp",
                    event: "whatsapp.message-received",
                    implemented: false,
                    description: this.$t("automation.whenNewWpReceived"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "reservation",
                    event: "reservation.created",
                    implemented: false,
                    description: this.$t("automation.whenNewReservationCreated"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "chatbot",
                    event: "chatbot.message-received",
                    implemented: false,
                    description: this.$t("auto.eventDescriptionNewChatbotMessageReceived"),
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "youtube",
                    event: "youtube.comment_received",
                    implemented: true,
                    description: "YouTube yorum geldiğinde",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "google_business",
                    event: "google_business.review_received",
                    implemented: true,
                    description: "Google Business yorum geldiğinde",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "google_play",
                    event: "google_play.review_received",
                    implemented: true,
                    description: "Google Play yorum geldiğinde",
                },
                {
                    module: "app_store",
                    event: "app_store.review_received",
                    implemented: true,
                    description: "App Store yorum geldiğinde",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "google_drive",
                    event: "google_drive.file_created",
                    implemented: true,
                    description: "Google Drive'a yeni dosya yüklendiğinde",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
                {
                    module: "google_drive",
                    event: "google_drive.file_updated",
                    implemented: true,
                    description: "Google Drive'da dosya güncellendiğinde",
                    payload: {
                        filter: {
                            all: true,
                        },
                    },
                },
            ],
            checkboxOption: {
                selectedRowKeys: [],
                // row select change event
                selectedRowChange: ({ row, isSelected, selectedRowKeys }) => {
                    console.log("selectedRowChange", { row, isSelected, selectedRowKeys });
                    this.checkboxOption.selectedRowKeys = selectedRowKeys;
                },
                // selected all change event
                selectedAllChange: ({ isSelected, selectedRowKeys }) => {
                    console.log("selectedAllChange", { isSelected, selectedRowKeys });
                    this.checkboxOption.selectedRowKeys = selectedRowKeys;
                },
            },
            rowStyleOption: {
                stripe: true,
            },
            clipboardOption: {
                copy: true,
                paste: false,
                cut: false,
                delete: false,
            },
            pagination: {
                page: 1,
                perPage: 20,
                total: 0,
                defaultPageSize: 50,
                hideOnSinglePage: true,
                pageSizeOptions: [10, 15, 20, 50, 100],
                showQuickJumper: true,
                showSizeChanger: true,
            },
        };
    },
    methods: {
        save(eventOptions) {
            const meta = this.events.find((event) => event.event === this.selectedEvent);

            // message.matched için configuration gerekli
            if (this.selectedEvent === "message.matched") {
                this.$emit("save", {
                    name: this.automationName,
                    event: this.selectedEvent,
                    payload: meta.payload,
                    requiresConfiguration: true,
                    eventOptions: eventOptions || this.messageMatchedConfig,
                });
            } else {
                this.$emit("save", {
                    name: this.automationName,
                    event: this.selectedEvent,
                    payload: meta.payload,
                });
            }
        },
        selectEvent(event) {
            this.selectedEvent = event;

            // message.matched için configuration modalını aç
            if (event === "message.matched") {
                this.showMessageMatchedConfigModal = true;
            } else {
                this.save();
            }
        },
        getCols() {
            return [
                {
                    key: "module",
                    field: "module",
                    title: this.$t("message.module"),
                    width: "",
                    align: "left",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return <span>{row.module}</span>;
                    },
                },
                {
                    key: "Event",
                    field: "event",
                    title: this.$t("report.event"),
                    width: "",
                    align: "left",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return <span>{row.event}</span>;
                    },
                },

                {
                    key: "description",
                    field: `description`,
                    title: this.$t("message.description"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return <span>{row.description}</span>;
                    },
                },
                {
                    key: "implemented",
                    field: "implemented",
                    title: this.$t("message.implemented"),
                    width: "120px",
                    align: "center",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return row.implemented ? (
                            <a-icon type="check-circle" style="color: green; font-size: 20px;" />
                        ) : (
                            <a-icon type="close-circle" style="color: red; font-size: 20px;" />
                        );
                    },
                },
                {
                    title: this.$t("message.action"),
                    key: "operation",
                    width: "100px",
                    align: "center",
                    operationColumn: true,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return (
                            <div class="actions">
                                <SupsisButton onClick={() => this.selectEvent(row.event)} ghost primary>
                                    {this.$t("chatbotv2.select")}
                                </SupsisButton>
                            </div>
                        );
                    },
                },
            ];
        },
        getEvents() {
            return this.events.filter((event) => {
                if (this.selectedModule) {
                    return this.selectedModule === "all" || event.module === this.selectedModule;
                }
                return true;
            });
        },
        getModuleIcon(module) {
            const iconMap = {
                all: { antIcon: "appstore" },
                chat: { customIcon: "Comments" },
                contact: { antIcon: "user" },
                tag: { antIcon: "tag" },
                email: { customIcon: "MailIcon" },
                task: { customIcon: "TaskIcon" },
                event: { antIcon: "calendar" },
                form: { customIcon: "FormIcon" },
                "meta-lead": { antIcon: "funnel-plot" },
                agent: { antIcon: "team" },
                invoice: { antIcon: "file-text" },
                whatsapp: { antIcon: "whats-app" },
                reservation: { antIcon: "schedule" },
                chatbot: { customIcon: "ChatbotIcon" },
                webhook: { antIcon: "api" },
                table: { antIcon: "table" },
                ticket: { customIcon: "TicketIcon" },
                calendar: { customIcon: "CalendarIcon" },
                voiceagent: { materialIcon: "record_voice_over" },
                webphone: { materialIcon: "phone" },
                message: { antIcon: "message" },
                channel: { materialIcon: "settings_input_antenna" },
                youtube: { antIcon: "youtube" },
                google_business: { antIcon: "google" },
                google_play: { antIcon: "android" },
                app_store: { antIcon: "apple" },
                google_drive: { antIcon: "google" },
            };
            return iconMap[module] || { antIcon: "question-circle" };
        },
        getModuleDisplayName(module) {
            const nameMap = {
                all: "Tümü",
                chat: "Chat",
                contact: "Kişi",
                tag: "Etiket",
                message: "Mesaj",
                email: "E-posta",
                task: "Görev",
                event: "Etkinlik",
                form: "Form",
                "meta-lead": "Meta Lead",
                agent: "Ajan",
                invoice: "Fatura",
                whatsapp: "WhatsApp",
                reservation: "Rezervasyon",
                chatbot: "Chatbot",
                webhook: "Webhook",
                table: "Tablo",
                ticket: "Ticket",
                calendar: "Takvim",
                voiceagent: "Ses Ajanı",
                webphone: "Webphone",
                channel: "Kanal",
                youtube: "YouTube",
                google_business: "Google Business",
                google_play: "Google Play",
                app_store: "App Store",
                google_drive: "Google Drive",
            };
            return nameMap[module] || module;
        },
        saveMessageMatchedConfig(config) {
            this.showMessageMatchedConfigModal = false;
            this.messageMatchedConfig = config;
            this.save(config);
        },
        cancelMessageMatchedConfig() {
            this.showMessageMatchedConfigModal = false;
            this.selectedEvent = null;
            this.messageMatchedConfig = {
                author: "all",
                messageType: "all",
                platform: "all",
                channels: [],
                textFilter: {
                    type: "contains",
                    value: "",
                    caseSensitive: false,
                },
            };
        },
    },
    watch: {
        selectedModule() {
            this.pagination.page = 1;
            if (this.automationName.includes("Untitled")) {
                this.automationName = `Untitled Automation 🚀`;
            }
        },
    },
});
</script>

<style scoped lang="scss">
.event-selector {
    display: flex;
    flex-direction: column;
    gap: 15px;

    .event-selector-toolbar {
        width: 100%;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: 10px;

        .field {
            display: flex;
            align-items: baseline;
            gap: 5px;
        }

        label {
            font-weight: 700;
            font-size: 16px;
        }
    }

    .module-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 10px;

        .module-chip {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 8px 12px;
            background: #f5f5f5;
            border: 1px solid #d9d9d9;
            border-radius: 16px;
            cursor: pointer;
            transition: all 0.3s ease;
            font-size: 14px;
            font-weight: 500;
            color: #666;

            &:hover {
                background: #e6f7ff;
                border-color: #91d5ff;
                color: #1890ff;
            }

            &.active {
                background: #1890ff;
                border-color: #1890ff;
                color: white;

                .chip-icon {
                    color: white;
                }
            }

            .chip-icon {
                font-size: 16px;
                color: #666;
                transition: color 0.3s ease;

                &.custom-icon {
                    width: 16px;
                    height: 16px;

                    :deep(svg) {
                        width: 100%;
                        height: 100%;

                        path {
                            fill: currentColor;
                        }
                    }
                }
            }

            .chip-text {
                white-space: nowrap;
            }
        }
    }
}
</style>
